import { EditorStarterItem } from '../../items/item-type';
import { EditorState, TrackType } from '../types';

/**
 * Magnetic 트랙에 아이템을 삽입할 때 기존 아이템들을 뒤로 밀어냅니다.
 *
 * @param state - 현재 에디터 상태
 * @param trackId - 삽입할 트랙 ID
 * @param insertFrame - 삽입 위치 (프레임)
 * @param insertDuration - 삽입할 아이템의 길이 (프레임)
 * @param excludeItemId - 제외할 아이템 ID (드래그 중인 아이템)
 * @returns 수정된 에디터 상태
 *
 * Before: [item1: 0-30] [item2: 30-60] [item3: 60-90]
 *         삽입 위치: 30, 길이: 20
 * After:  [item1: 0-30] [NEW: 30-50] [item2: 50-80] [item3: 80-110]
 */
export const pushItemsBackOnMagneticTrack = (
  state: EditorState,
  trackId: string,
  insertFrame: number,
  insertDuration: number,
  excludeItemId?: string,
): EditorState => {
  const track = state.undoableState.tracks.find((t) => t.id === trackId);
  if (!track || !track.magnetic) {
    return state;
  }

  // 트랙의 아이템들을 시작 프레임 순으로 정렬 (제외 아이템 빼고)
  const sortedItems = track.items
    .filter((id) => id !== excludeItemId)
    .map((id) => state.undoableState.items[id])
    .filter((item): item is EditorStarterItem => item !== undefined)
    .sort((a, b) => a.from - b.from);

  if (sortedItems.length === 0) {
    return state;
  }

  const updatedItems: Record<string, EditorStarterItem> = {};
  let hasChanges = false;

  // 삽입 위치 이후의 아이템들을 뒤로 밀기
  for (const item of sortedItems) {
    // 삽입 위치 이후에 시작하거나 겹치는 아이템들
    if (item.from >= insertFrame) {
      const newFrom = item.from + insertDuration;
      if (item.from !== newFrom) {
        updatedItems[item.id] = {
          ...item,
          from: newFrom,
        };
        hasChanges = true;
      }
    } else if (item.from + item.durationInFrames > insertFrame) {
      // 삽입 위치와 겹치는 아이템도 뒤로 밀기
      const newFrom = insertFrame + insertDuration;
      updatedItems[item.id] = {
        ...item,
        from: newFrom,
      };
      hasChanges = true;
    }
  }

  if (!hasChanges) {
    return state;
  }

  return {
    ...state,
    undoableState: {
      ...state.undoableState,
      items: {
        ...state.undoableState.items,
        ...updatedItems,
      },
    },
  };
};

/**
 * Magnetic 트랙인지 확인합니다.
 */
export const isTrackMagnetic = (
  state: EditorState,
  trackId: string,
): boolean => {
  const track = state.undoableState.tracks.find((t) => t.id === trackId);
  return track?.magnetic ?? false;
};

/**
 * Magnetic 트랙에서 아이템을 오른쪽으로 확장할 때 뒤에 있는 아이템들을 밀어냅니다.
 * 아이템들을 확장된 아이템 끝에 바로 붙여서 배치합니다.
 *
 * @param state - 현재 에디터 상태
 * @param trackId - 트랙 ID
 * @param extendedItemId - 확장된 아이템 ID
 * @param newEndFrame - 확장 후 아이템의 새로운 끝 프레임
 * @param originalEndFrame - 확장 전 아이템의 원래 끝 프레임 (사용하지 않지만 호출 일관성을 위해 유지)
 * @returns 수정된 에디터 상태
 *
 * Before: [A: 0-30] [B: 30-60] [C: 60-90]
 *         A를 50까지 확장
 * After:  [A: 0-50] [B: 50-80] [C: 80-110]
 */
export const pushItemsForMagneticExtendRight = (
  state: EditorState,
  trackId: string,
  extendedItemId: string,
  newEndFrame: number,
  _originalEndFrame: number,
): EditorState => {
  const track = state.undoableState.tracks.find((t) => t.id === trackId);
  if (!track || !track.magnetic) {
    return state;
  }

  const items = state.undoableState.items;
  const extendedItem = items[extendedItemId];
  if (!extendedItem) {
    return state;
  }

  // 트랙의 아이템들을 시작 프레임 순으로 정렬
  const sortedItems = track.items
    .map((id) => items[id])
    .filter((item): item is EditorStarterItem => item !== undefined)
    .sort((a, b) => a.from - b.from);

  // 확장된 아이템 이후의 아이템들을 연속으로 배치
  const updatedItems: Record<string, EditorStarterItem> = {};
  let hasChanges = false;
  let foundExtendedItem = false;
  let nextAvailableFrame = newEndFrame;

  for (const item of sortedItems) {
    if (item.id === extendedItemId) {
      foundExtendedItem = true;
      continue;
    }

    // 확장된 아이템 이후의 모든 아이템들을 연속 배치
    if (foundExtendedItem) {
      if (item.from !== nextAvailableFrame) {
        updatedItems[item.id] = {
          ...item,
          from: nextAvailableFrame,
        };
        hasChanges = true;
      }
      nextAvailableFrame += item.durationInFrames;
    }
  }

  if (!hasChanges) {
    return state;
  }

  return {
    ...state,
    undoableState: {
      ...state.undoableState,
      items: {
        ...state.undoableState.items,
        ...updatedItems,
      },
    },
  };
};

/**
 * Magnetic 트랙에서 아이템을 왼쪽으로 확장할 때 뒤에 있는 아이템들을 밀어냅니다.
 * 아이템들을 확장된 아이템 끝에 바로 붙여서 배치합니다.
 *
 * @param state - 현재 에디터 상태
 * @param trackId - 트랙 ID
 * @param extendedItemId - 확장된 아이템 ID
 * @param _newStartFrame - 확장 후 아이템의 새로운 시작 프레임 (사용하지 않지만 호출 일관성을 위해 유지)
 * @param _originalStartFrame - 확장 전 아이템의 원래 시작 프레임 (사용하지 않지만 호출 일관성을 위해 유지)
 * @returns 수정된 에디터 상태
 *
 * Before: [A: 0-30] [B: 30-60] [C: 60-90]
 *         B를 20부터 시작하도록 확장 (duration 40으로 증가)
 * After:  [A: 0-30] [B: 20-60] [C: 60-100] → 뒤로 밀기
 */
export const pushItemsForMagneticExtendLeft = (
  state: EditorState,
  trackId: string,
  extendedItemId: string,
  _newStartFrame: number,
  _originalStartFrame: number,
): EditorState => {
  const track = state.undoableState.tracks.find((t) => t.id === trackId);
  if (!track || !track.magnetic) {
    return state;
  }

  const items = state.undoableState.items;
  const extendedItem = items[extendedItemId];
  if (!extendedItem) {
    return state;
  }

  // 확장된 아이템의 새로운 끝 프레임
  const extendedItemEndFrame =
    extendedItem.from + extendedItem.durationInFrames;

  // 트랙의 아이템들을 시작 프레임 순으로 정렬
  const sortedItems = track.items
    .map((id) => items[id])
    .filter((item): item is EditorStarterItem => item !== undefined)
    .sort((a, b) => a.from - b.from);

  // 확장된 아이템 이후의 아이템들을 연속으로 배치
  const updatedItems: Record<string, EditorStarterItem> = {};
  let hasChanges = false;
  let foundExtendedItem = false;
  let nextAvailableFrame = extendedItemEndFrame;

  for (const item of sortedItems) {
    if (item.id === extendedItemId) {
      foundExtendedItem = true;
      continue;
    }

    // 확장된 아이템 이후의 모든 아이템들을 연속 배치
    if (foundExtendedItem) {
      if (item.from !== nextAvailableFrame) {
        updatedItems[item.id] = {
          ...item,
          from: nextAvailableFrame,
        };
        hasChanges = true;
      }
      nextAvailableFrame += item.durationInFrames;
    }
  }

  if (!hasChanges) {
    return state;
  }

  return {
    ...state,
    undoableState: {
      ...state.undoableState,
      items: {
        ...state.undoableState.items,
        ...updatedItems,
      },
    },
  };
};

/**
 * 트랙 인덱스로 Magnetic 여부를 확인합니다.
 */
export const isTrackMagneticByIndex = (
  tracks: TrackType[],
  trackIndex: number,
): boolean => {
  const track = tracks[trackIndex];
  return track?.magnetic ?? false;
};

/**
 * 특정 트랙의 아이템들을 앞으로 당겨 간격을 제거합니다.
 *
 * Before: [item1: 0-30] [gap: 30-50] [item2: 50-100] [gap: 100-120] [item3: 120-150]
 * After:  [item1: 0-30] [item2: 30-80] [item3: 80-110]
 */
export const collapseTrackGaps = (
  state: EditorState,
  trackId: string,
): EditorState => {
  const track = state.undoableState.tracks.find((t) => t.id === trackId);
  if (!track || track.items.length === 0) {
    return state;
  }

  // 아이템들을 시작 프레임 순으로 정렬
  const sortedItems = track.items
    .map((id) => state.undoableState.items[id])
    .filter((item): item is EditorStarterItem => item !== undefined)
    .sort((a, b) => a.from - b.from);

  if (sortedItems.length === 0) {
    return state;
  }

  // 순차적으로 앞으로 당기기
  let nextStartFrame = 0;
  const updatedItems: Record<string, EditorStarterItem> = {};
  let hasChanges = false;

  for (const item of sortedItems) {
    if (item.from !== nextStartFrame) {
      updatedItems[item.id] = {
        ...item,
        from: nextStartFrame,
      };
      hasChanges = true;
    }
    nextStartFrame += item.durationInFrames;
  }

  // 변경이 없으면 원래 state 반환
  if (!hasChanges) {
    return state;
  }

  // state 업데이트
  return {
    ...state,
    undoableState: {
      ...state.undoableState,
      items: {
        ...state.undoableState.items,
        ...updatedItems,
      },
    },
  };
};

/**
 * 모든 magnetic 트랙의 간격을 제거합니다.
 */
export const collapseAllMagneticTrackGaps = (
  state: EditorState,
): EditorState => {
  let currentState = state;

  for (const track of state.undoableState.tracks) {
    if (track.magnetic) {
      currentState = collapseTrackGaps(currentState, track.id);
    }
  }

  return currentState;
};

/**
 * 드래그 시 magnetic 트랙에 아이템들이 놓일 때 기존 아이템들을 뒤로 밀어냅니다.
 *
 * @param items - 현재 아이템들 (변경 가능)
 * @param tracks - 현재 트랙들
 * @param draggedPositions - 드래그된 아이템들의 새 위치 정보
 * @param draggedItemIds - 드래그 중인 아이템 ID들
 * @param trackInsertions - 트랙 삽입 정보 (인덱스 보정용)
 * @returns 수정된 아이템들
 *
 * 예시:
 * - 기존 트랙: [A: 0-30] [B: 30-60] [C: 60-90]
 * - 드래그하여 30 위치에 20프레임짜리 아이템 놓기
 * - 결과: [A: 0-30] [NEW: 30-50] [B: 50-80] [C: 80-110]
 */
export const pushItemsBackForMagneticDrag = ({
  items,
  tracks,
  draggedPositions,
  draggedItemIds,
  trackInsertions,
}: {
  items: Record<string, EditorStarterItem>;
  tracks: TrackType[];
  draggedPositions: Array<{
    id: string;
    from: number;
    durationInFrames: number;
    trackIndex: number;
  }>;
  draggedItemIds: string[];
  trackInsertions?: TrackInsertions | null;
}): Record<string, EditorStarterItem> => {
  const updatedItems: Record<string, EditorStarterItem> = { ...items };

  // 각 드래그된 아이템이 놓일 트랙별로 처리 (원본 트랙 인덱스로 변환)
  const positionsByOriginalTrack = new Map<
    number,
    Array<{ from: number; durationInFrames: number }>
  >();

  for (const pos of draggedPositions) {
    // 트랙 삽입이 있는 경우 원본 인덱스로 변환
    const originalTrackIndex = calculateOriginalTrackIndex(
      pos.trackIndex,
      trackInsertions ?? null,
    );

    // 새로 생성되는 트랙(-1)이면 스킵 (기존 아이템이 없음)
    if (originalTrackIndex < 0) {
      continue;
    }

    const trackPositions =
      positionsByOriginalTrack.get(originalTrackIndex) || [];
    trackPositions.push({
      from: pos.from,
      durationInFrames: pos.durationInFrames,
    });
    positionsByOriginalTrack.set(originalTrackIndex, trackPositions);
  }

  // 각 트랙별로 magnetic 여부 확인 및 처리
  for (const [
    originalTrackIndex,
    positions,
  ] of positionsByOriginalTrack.entries()) {
    const track = tracks[originalTrackIndex];

    if (!track || !track.magnetic) {
      continue; // Magnetic 트랙이 아니면 스킵
    }

    // 해당 트랙의 기존 아이템들 (드래그 중인 아이템 제외)
    const existingItems = track.items
      .filter((id) => !draggedItemIds.includes(id))
      .map((id) => items[id])
      .filter((item): item is EditorStarterItem => item !== undefined)
      .sort((a, b) => a.from - b.from);

    if (existingItems.length === 0) {
      continue;
    }

    // 드래그된 아이템들의 위치 정보를 정렬
    const sortedPositions = [...positions].sort((a, b) => a.from - b.from);

    // 각 드래그 위치에 대해 뒤에 있는 아이템들을 밀기
    for (const dragPos of sortedPositions) {
      const dragEnd = dragPos.from + dragPos.durationInFrames;

      for (const existingItem of existingItems) {
        const currentItem = updatedItems[existingItem.id];
        if (!currentItem) continue;

        const existingStart = currentItem.from;
        const existingEnd = existingStart + currentItem.durationInFrames;

        // Case 1: 기존 아이템이 드롭 위치 이후에 시작하는 경우
        // → 드롭 아이템 길이만큼 뒤로 밀기
        if (existingStart >= dragPos.from) {
          const newFrom = existingStart + dragPos.durationInFrames;
          updatedItems[existingItem.id] = {
            ...currentItem,
            from: newFrom,
          };
        }
        // Case 2: 기존 아이템이 드롭 위치보다 앞에서 시작하지만 드롭 위치를 넘어가는 경우
        // → 드롭 아이템 끝 위치로 이동
        else if (existingEnd > dragPos.from) {
          updatedItems[existingItem.id] = {
            ...currentItem,
            from: dragEnd,
          };
        }
        // Case 3: 기존 아이템이 드롭 위치보다 완전히 앞에 있는 경우
        // → 그대로 유지 (아무것도 안 함)
      }
    }
  }

  return updatedItems;
};

/**
 * 트랙 삽입 정보를 기반으로 원본 트랙 인덱스를 계산합니다.
 */
const calculateOriginalTrackIndex = (
  currentIndex: number,
  trackInsertions: TrackInsertions | null,
): number => {
  if (!trackInsertions) {
    return currentIndex;
  }

  switch (trackInsertions.type) {
    case 'top':
      // 상단에 트랙이 삽입되면 기존 트랙들의 인덱스가 증가
      return currentIndex - trackInsertions.count;
    case 'bottom':
      // 하단 삽입은 기존 인덱스에 영향 없음
      return currentIndex;
    case 'between':
      if (currentIndex < trackInsertions.trackIndex) {
        // 삽입 지점 이전은 원래 인덱스 유지
        return currentIndex;
      }
      if (currentIndex < trackInsertions.trackIndex + trackInsertions.count) {
        // 새로운 트랙은 원본 인덱스가 없음
        return -1;
      }
      // 삽입 지점 이후는 카운트만큼 뒤로
      return currentIndex - trackInsertions.count;
    default:
      return currentIndex;
  }
};

type TrackInsertions =
  | { type: 'top'; count: number }
  | { type: 'bottom'; count: number }
  | { type: 'between'; trackIndex: number; count: number }
  | null;

export type PushedBackItemPreview = {
  id: string;
  trackIndex: number;
  originalFrom: number;
  newFrom: number;
  durationInFrames: number;
};

/**
 * 드래그 중 magnetic 트랙에서 밀려나는 아이템들의 프리뷰 위치를 계산합니다.
 * 실제로 상태를 변경하지 않고 프리뷰용 정보만 반환합니다.
 */
export const calculatePushedBackItemsPreview = ({
  items,
  tracks,
  draggedPositions,
  draggedItemIds,
  trackInsertions,
}: {
  items: Record<string, EditorStarterItem>;
  tracks: TrackType[];
  draggedPositions: Array<{
    id: string;
    from: number;
    durationInFrames: number;
    trackIndex: number;
  }>;
  draggedItemIds: string[];
  trackInsertions?: TrackInsertions | null;
}): PushedBackItemPreview[] => {
  const pushedBackItems: PushedBackItemPreview[] = [];

  // 각 드래그된 아이템이 놓일 트랙별로 처리 (원본 트랙 인덱스로 변환)
  const positionsByOriginalTrack = new Map<
    number,
    Array<{ from: number; durationInFrames: number }>
  >();

  for (const pos of draggedPositions) {
    const originalTrackIndex = calculateOriginalTrackIndex(
      pos.trackIndex,
      trackInsertions ?? null,
    );

    if (originalTrackIndex < 0) {
      continue;
    }

    const trackPositions =
      positionsByOriginalTrack.get(originalTrackIndex) || [];
    trackPositions.push({
      from: pos.from,
      durationInFrames: pos.durationInFrames,
    });
    positionsByOriginalTrack.set(originalTrackIndex, trackPositions);
  }

  // 각 트랙별로 magnetic 여부 확인 및 처리
  for (const [
    originalTrackIndex,
    positions,
  ] of positionsByOriginalTrack.entries()) {
    const track = tracks[originalTrackIndex];
    if (!track || !track.magnetic) {
      continue;
    }

    // 해당 트랙의 기존 아이템들 (드래그 중인 아이템 제외)
    const existingItems = track.items
      .filter((id) => !draggedItemIds.includes(id))
      .map((id) => items[id])
      .filter((item): item is EditorStarterItem => item !== undefined)
      .sort((a, b) => a.from - b.from);

    if (existingItems.length === 0) {
      continue;
    }

    const sortedPositions = [...positions].sort((a, b) => a.from - b.from);

    // 임시로 각 아이템의 새 위치를 추적
    const itemNewPositions = new Map<string, number>();
    for (const item of existingItems) {
      itemNewPositions.set(item.id, item.from);
    }

    for (const dragPos of sortedPositions) {
      const dragEnd = dragPos.from + dragPos.durationInFrames;

      for (const existingItem of existingItems) {
        const currentFrom =
          itemNewPositions.get(existingItem.id) ?? existingItem.from;
        const existingEnd = currentFrom + existingItem.durationInFrames;

        let newFrom: number | null = null;

        if (currentFrom >= dragPos.from) {
          newFrom = currentFrom + dragPos.durationInFrames;
        } else if (existingEnd > dragPos.from) {
          newFrom = dragEnd;
        }

        if (newFrom !== null) {
          itemNewPositions.set(existingItem.id, newFrom);
        }
      }
    }

    // 원래 위치와 새 위치가 다른 아이템들만 프리뷰에 추가
    for (const existingItem of existingItems) {
      const newFrom =
        itemNewPositions.get(existingItem.id) ?? existingItem.from;
      if (newFrom !== existingItem.from) {
        pushedBackItems.push({
          id: existingItem.id,
          trackIndex: originalTrackIndex,
          originalFrom: existingItem.from,
          newFrom,
          durationInFrames: existingItem.durationInFrames,
        });
      }
    }
  }

  return pushedBackItems;
};
